import { BookOutlined } from '@ant-design/icons'
import { useFormatter } from '@api/useFormatter'
import { OrderModalButton } from '@components/_SiteWide/OrderDisplay/OrderModalButton'
import { addCommasToNumber, GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Col, Form, Row, Tooltip } from 'antd'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useSecurityContext } from '../../../../Navigation/SecurityContext'
import { MetricDetail } from '../MetricDetail'
import { editableFields, OrderPayrollFormType, PayPeriodOrderPay } from '../types'
import { usePayroll } from '@/api/usePayroll'
import { NotePopover } from '../NotePopover'
import { PeriodDetailsEditButtons } from '../PeriodDetailsEditButtons'
import { FormInstance } from 'antd/lib'
import { isDefined } from '@/utils/general'

type OrderFormProps = {
  date: string
  driverId: number
  order: PayPeriodOrderPay
  setSelectedOrder: (orderId: string) => void
  setShowingModal: (showing: boolean) => void
}

export function OrderForm({ date, driverId, order, setSelectedOrder, setShowingModal }: OrderFormProps) {
  const [form] = Form.useForm<OrderPayrollFormType>()
  const [editing, setEditing] = useState(false)

  const { overrideOrderPayDetailMutation } = usePayroll()
  const { mutateAsync: overrideOrder } = overrideOrderPayDetailMutation()

  const initialValues = useMemo(() => {
    const formValues = order.pay_items.reduce(
      (acc, item) => {
        if (!editableFields.includes(item.name)) return acc
        acc[item.name] = item.override_quantity ?? item.units ?? 0
        return acc
      },
      {} as Record<string, string | number | null | undefined>
    )
    formValues.override_reason = order.override_reason
    return formValues
  }, [order.pay_items])

  async function handleSave(formData: OrderPayrollFormType) {
    const req = {
      date,
      driver_id: driverId,
      order_id: order.order_id,
      gallons: formData.Gallons,
      stops: formData.Stops,
      loads: formData.Loads,
      drops: formData.Drops,
      reason: formData.override_reason,
      deemed_hours: formData['Deemed Hours'],
    }
    await overrideOrder(req)
    resetForm()
  }

  function resetForm() {
    form.setFieldsValue(initialValues)
  }

  return (
    <Form
      key={order.order_no}
      id={`order-form-${order.order_no}`}
      form={form}
      onFinish={handleSave}
      onReset={resetForm}
      initialValues={initialValues}
    >
      <Row className='pay-row'>
        <Col span={8} className='pay-summary bg-2 vertical-flex-center'>
          <OrderHeaderColumn
            form={form}
            order={order}
            setSelectedOrder={setSelectedOrder}
            setShowingModal={setShowingModal}
            editing={editing}
            setEditing={setEditing}
          />
        </Col>
        <Col span={16}>
          <Row>
            {order.pay_items.map((detail, index) => (
              <MetricDetail
                key={index}
                detail={detail}
                count={order.pay_items.length}
                index={index}
                editing={editing}
              />
            ))}
          </Row>
        </Col>
      </Row>
    </Form>
  )
}

type OrderHeaderColumnProps = {
  form: FormInstance<OrderPayrollFormType>
  order: PayPeriodOrderPay
  setSelectedOrder: (orderId: string) => void
  setShowingModal: (showing: boolean) => void
  editing: boolean
  setEditing: (editing: boolean) => void
}
function OrderHeaderColumn({
  form,
  order,
  setSelectedOrder,
  setShowingModal,
  editing,
  setEditing,
}: OrderHeaderColumnProps) {
  const { scopes } = useSecurityContext()
  const { formatCurrency } = useFormatter()
  const formattedCurrency = formatCurrency(null, true)
  const canViewBOL = scopes.order_movements?.view || scopes.order_movements?.edit
  const hasBeenOverridden = isDefined(order.override_order_total)

  return (
    <Row>
      <Col className='vertical-flex-center' span={3}>
        <Horizontal verticalCenter gap={8}>
          <OrderModalButton
            orderId={order.order_id}
            showModal={() => {
              setSelectedOrder(order.order_id)
              setShowingModal(true)
            }}
          />
          <Tooltip title='View BOL' mouseEnterDelay={0.5} placement='bottom'>
            {canViewBOL && (
              <Link
                to={`/order_movements_page/report?${new URLSearchParams({ order_number: order.order_no })}`}
                target='_blank'
              >
                <GraviButton size='small' icon={<BookOutlined />} />
              </Link>
            )}
          </Tooltip>
        </Horizontal>
      </Col>

      <Col className='pay-period-group-border vertical-flex-center pr-4' span={6}>
        <Texto align='right' category='label' appearance='medium'>
          ORDER #
        </Texto>
        <Texto align='right' weight={'bold'} category='p2'>
          {order.order_no}
        </Texto>
      </Col>
      <Col className='pay-period-group-border vertical-flex-center pr-4' span={12}>
        <Horizontal verticalCenter gap={12}>
          <Vertical>
            <Texto align='right' category='label' appearance='medium'>
              ORDER PAY
            </Texto>
            {hasBeenOverridden ? (
              <Vertical className='value' justifyContent='flex-end' alignItems='flex-end'>
                <Texto category='label' appearance='light' style={{ textDecoration: 'line-through' }}>
                  {formattedCurrency}
                  {addCommasToNumber(order.order_total, 2)}
                </Texto>
                <Texto align='right' weight={'bold'} category='p2'>
                  {formattedCurrency}
                  {addCommasToNumber(order.override_order_total, 2)}
                </Texto>
              </Vertical>
            ) : (
              <Texto align='right' weight={'bold'} category='p2'>
                {formattedCurrency}
                {addCommasToNumber(order.order_total, 2)}
              </Texto>
            )}
          </Vertical>
          {hasBeenOverridden && <NotePopover order={order} />}
        </Horizontal>
      </Col>
      <Col span={3} className='vertical-flex-center items-center '>
        <PeriodDetailsEditButtons
          editing={editing}
          setEditing={setEditing}
          overrideReason={order.override_reason}
          form={form}
          payrollType='order'
        />
      </Col>
    </Row>
  )
}
